import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import _ from "lodash";
import moment from "moment";
import { Box } from "@mui/material";
import {
  MyLeaveAllLeavesWithCoverage,
  MyLeavesAllLeavesCard,
} from "my-leave/components";
import { useMyLeaveEvents, useMyLeaveUsersWithRequest } from "my-leave/hooks";
import { OcInfoDialog, OcPageNameTitle, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcClientState,
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";
//import { getFilterValues } from "oc/utils";
import { myLeaveCurrentDateState } from "states";

export const MyLeaveApproveLeaveRequestsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [currentDate, setCurrentDate] = useRecoilState(myLeaveCurrentDateState);
  const [showDialog, setShowDialog] = useState(false);

  let { contractCode: contract } = useOcCurrentUserState();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract,
    product: "MyLeave",
    role: "examiner",
  });

  const [onlyRequested, setOnlyRequested] = useOcClientState<boolean>(
    "showOnlyRequestedLeaves",
    true,
  );

  const [prevOnlyRequested, setPrevOnlyRequested] = useOcClientState<boolean>(
    "prevShowOnlyRequestedLeaves",
    true,
  );

  const [filterable, setFilterable] = useOcClientState<boolean>(
    "allLeavesFilter",
    false,
  );

  const [filterItemErrors, setFilterItemErrors] = useState<string[]>([]);

  let tableId = "allLeavesTableData";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });
  const [prevTableData, setPrevTableData] = useOcTableState(
    `${tableId}Prev`,
    {
      ...initialTableData,
      sortField: "name",
      pageSize: 5,
    },
  );

  const [showRemoteWorks, setShowRemoteWorks] = useOcClientState<boolean>(
    "showRemoteWorks",
    true,
  );

  const [prevShowRemoteWorks, setPrevShowRemoteWorks] =
    useOcClientState<boolean>("prevShowRemoteWorks", true);

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  const {
    loadQuery: loadUsers,
    data,
    loading,
  } = useMyLeaveUsersWithRequest({
    onlyRequested,
    roleFilters,
    tableData,
    showRemoteWorks,
    role: "examiner",
    startDate: moment().startOf("year").toDate(),
    endDate: moment().endOf("year").add(1, "year").toDate(),
    onCompleted: handleCompleted,
  });

  const {
    loadEvents,
    data: leavesData,
    loading: leaveEventsLoading,
    eventsByDays,
  } = useMyLeaveEvents({
    roleFilters,
    currentDate: moment(currentDate).toDate(),
    viewedByAdmin: false,
    contract: contract,
    role: "examiner",
  });

  const MAX_ITEM_COUNT = 1000;
  const generateRoleFilterError = () => {
    const newState = [...filterItemErrors];
    if ((roleFilters?.company?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_COMPANY"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.orgUnit?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_ORGUNIT"));
      setFilterItemErrors(newState);
    }

    if ((roleFilters?.workingPlace?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("WORKINGPLACE"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.jobClassGroup?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_JOBCLASSGROUP"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.payOffice?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_PAYOFFICE"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.contract?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_GET_EMPLOYEE"));
      setFilterItemErrors(newState);
    }

    return newState?.length > 0 ? newState : false;
  };

  const handleLoadQuery = () => {
    const isItemError = generateRoleFilterError();
    if (isItemError) {
      setShowDialog(true);
      handleChangeFilterable(false);
      return;
    }
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
    setPrevShowRemoteWorks(showRemoteWorks);
    setPrevOnlyRequested(onlyRequested);
  };

  const buttonDisable =
    _.isEqual(roleFilters, prevRoleFilters) &&
    showRemoteWorks === prevShowRemoteWorks &&
    onlyRequested === prevOnlyRequested;

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(
        `/my-leave/user-requests/MyLeave/examiner/${o?.row?.contract}`,
      );
    } else {
      history.push(`/my-leave/user-requests/MyLeave/examiner/${o.contract}`);
    }
  };

  const onSelectRow = (o: { _id?: string; contract: string }) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? null : (o?._id ?? null));
    } else {
      originalonSelectRow(o);
    }
  };

  const handleOnlyReqestedChange = (state: boolean) => {
    setPrevOnlyRequested(onlyRequested);
    setOnlyRequested(state);
  };

  const handleCurrentDateChange = (date: Date | string) => {
    setCurrentDate(date);
  };

  const handleTableDataChange = (state: typeof initialTableData) => {
    const keys = Object.keys(state).toString();
    const newTableData = { ...tableData, ...state };
    setTableData(newTableData);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    loadUsers();
    loadEvents();
  };

  const handleShowRemoteWorksChange = (newValue: boolean) => {
    setShowRemoteWorks(newValue);
  };

  const handleClearFilters = () => {
    resetRoleFilters();
    handleChangeFilterable(false);
    setFilterItemErrors([]);
    // Reset showRemoteWorks to last saved value (prevShowRemoteWorks)
    setShowRemoteWorks(prevShowRemoteWorks);
  };

  const isTabledataChanged = !_.isEqual(tableData, prevTableData);
  useEffect(() => {
    if (isTabledataChanged) {
      handleLoadQuery();
      setPrevTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabledataChanged]);

  const handleDialogClose = () => {
    setShowDialog(false);
    setFilterItemErrors([]);
  };

  useEffect(() => {
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  // FIX: filterValues inicializálás useEffect-ben - háromféle opció a leaveBadges filter-hez
  useEffect(() => {
    if (
      tableData?.filterField === "leaveBadges" &&
      data?.rows?.length > 0
    ) {
      // Háromféle filter opció: past, current, future
      const customFilterValues = ["past", "current", "future"];

      if (!_.isEqual(tableData.filterValues, customFilterValues)) {
        setTableData({ filterValues: customFilterValues });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData?.filterField, data?.rows]);

  return (
    <OcPageTransition>
      <Box>
        <OcPageNameTitle title={t("OC_APPROVE_REQUESTS")} goBack={false} />
        <OcInfoDialog
          open={showDialog}
          onClose={handleDialogClose}
          text={t("OC_TOO_MANY_HITS")}
          handleDialogClose={handleDialogClose}
          isMobile={isMobile}
          filterItemErrors={filterItemErrors}
        />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          flexGrow={1}
        >
          <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
            <MyLeavesAllLeavesCard
              buttonDisable={buttonDisable}
              contract={contract}
              currentDate={currentDate}
              data={data}
              examinerDasboard={false}
              filterable={filterable}
              label={t("MY_LEAVE_LEAVE_AND_ABSENCE_REQUESTS")}
              loading={loading}
              onAddFilter={handleAddFilter}
              onClearFilters={handleClearFilters}
              onCurrentDateChange={handleCurrentDateChange}
              onFilterableChange={handleChangeFilterable}
              onLoadQuery={handleLoadQuery}
              onlyRequested={onlyRequested}
              onOnlyRequestedChange={handleOnlyReqestedChange}
              onRemoveFilter={handleRemoveFilter}
              onSelectRow={onSelectRow}
              onRowSelection={onSelectRow}
              onShowRemoteWorksChange={handleShowRemoteWorksChange}
              onTableDataChange={handleTableDataChange}
              product={"MyLeave"}
              role={"examiner"}
              roleFilters={roleFilters}
              showRemoteWorks={showRemoteWorks}
              tableData={tableData}
              tableId={tableId}
              selectedRowId={selectedRowId ?? undefined}
              handleMoreButton={originalonSelectRow}
            />
            <Box height="16px" />
            <MyLeaveAllLeavesWithCoverage
              cardTitle={t("MY_LEAVE_LEAVES_AND_REMOTE_WORKS")}
              currentDate={currentDate}
              onCurrentDateChange={handleCurrentDateChange}
              monthFiltering={false}
              showRemoteWorks={prevShowRemoteWorks}
              viewedByAdmin={false}
              leavesData={leavesData}
              eventsByDays={eventsByDays}
              loading={leaveEventsLoading}
            />
          </Box>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveApproveLeaveRequestsPage;
